// src/lib/tin.ts — GL points + contour polylines → TIN interpolator (IDW fallback outside hull)
import { Delaunay } from 'd3-delaunay';
export type GLPoint={id:string;e:number;n:number;z:number};
export type GLContour={id:string;z:number;pts:{e:number;n:number}[]};
type S={e:number;n:number;z:number};
const DENSIFY=2.0; // m spacing along contour segments

export const collectSamples=(pts:GLPoint[],ctrs:GLContour[]):S[]=>{
  const s:S[]=pts.map(p=>({e:p.e,n:p.n,z:p.z}));
  ctrs.forEach(c=>{for(let i=0;i<c.pts.length;i++){
    const a=c.pts[i];s.push({e:a.e,n:a.n,z:c.z});
    const b=c.pts[i+1];if(!b)continue;
    const d=Math.hypot(b.e-a.e,b.n-a.n),k=Math.floor(d/DENSIFY);
    for(let j=1;j<=k;j++){const t=j/(k+1);
      s.push({e:a.e+(b.e-a.e)*t,n:a.n+(b.n-a.n)*t,z:c.z});}}});
  const seen=new Set<string>();
  return s.filter(p=>{const k=p.e.toFixed(2)+','+p.n.toFixed(2);
    if(seen.has(k))return false;seen.add(k);return true;});};

const idw=(s:S[],e:number,n:number)=>{
  const near=[...s].map(p=>({p,d:Math.hypot(p.e-e,p.n-n)}))
    .sort((a,b)=>a.d-b.d).slice(0,6);
  if(near[0].d<1e-6)return near[0].p.z;
  let wz=0,ws=0;near.forEach(({p,d})=>{const w=1/(d*d);wz+=w*p.z;ws+=w;});
  return wz/ws;};

export const buildInterpolator=(pts:GLPoint[],ctrs:GLContour[])=>{
  const s=collectSamples(pts,ctrs);
  if(s.length===0)return null;
  if(s.length<3)return (e:number,n:number)=>idw(s,e,n);
  const del=Delaunay.from(s,p=>p.e,p=>-p.n);   // flip N so triangulation is in screen-like plane
  const {points,triangles}=del;
  return (e:number,n:number):number=>{
    const y=-n;
    for(let t=0;t<triangles.length;t+=3){
      const i0=triangles[t],i1=triangles[t+1],i2=triangles[t+2];
      const x0=points[2*i0],y0=points[2*i0+1],x1=points[2*i1],y1=points[2*i1+1],
            x2=points[2*i2],y2=points[2*i2+1];
      const d=(y1-y2)*(x0-x2)+(x2-x1)*(y0-y2);
      if(Math.abs(d)<1e-12)continue;                 // degenerate (collinear) triangle
      const w0=((y1-y2)*(e-x2)+(x2-x1)*(y-y2))/d;
      const w1=((y2-y0)*(e-x2)+(x0-x2)*(y-y2))/d;
      const w2=1-w0-w1;
      if(w0>=-1e-9&&w1>=-1e-9&&w2>=-1e-9)
        return w0*s[i0].z+w1*s[i1].z+w2*s[i2].z;     // inside TIN → linear interp
    }
    return idw(s,e,n);                               // outside hull → IDW of 6 nearest
  };};